import React, { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { apiUrl } from "../ApiManager/AxiosHeaders";
import ApplicationCostSection from "./ApplicationCostSection";
import { useApplicationGst } from "./useApplicationGst";
import { buildApplicationCostPayload } from "../../utilis/gstCalculation";
import {
  getEffectiveBaseCost,
  getEffectiveCurrency,
  getEffectiveTax,
  getInitialCostStateFromDetails,
} from "../../utilis/applicationCostHelpers";

function ApplicationGstSetupModal({
  show,
  onClose,
  onSaved,
  appId,
  type,
  applicationDetails = {},
  suggestedBaseCost = "",
}) {
  const mergedDetails = useMemo(
    () => ({
      ...applicationDetails,
      gst_status:
        applicationDetails.gst_status ||
        applicationDetails.tax ||
        "",
      currency: applicationDetails.currency || "AUD",
    }),
    [applicationDetails],
  );

  const [cost, setCost] = useState("");
  const [currency, setCurrency] = useState("AUD");
  const [tax, setTax] = useState("");
  const [costError, setCostError] = useState("");
  const [currencyError, setCurrencyError] = useState("");
  const [taxError, setTaxError] = useState("");
  const [saving, setSaving] = useState(false);

  const {
    gstSetting,
    gstPercentage,
    breakdown,
    effectiveCurrency,
    applyCurrencySelection,
  } = useApplicationGst({
    cost,
    currency,
    tax,
    details: mergedDetails,
  });

  useEffect(() => {
    if (!show) return;

    const initial = getInitialCostStateFromDetails(mergedDetails);
    setCost(
      initial.cost ||
        (suggestedBaseCost ? String(suggestedBaseCost) : ""),
    );
    setCurrency(initial.currency || mergedDetails.currency || "AUD");
    setTax(initial.tax || mergedDetails.gst_status || "");
    setCostError("");
    setCurrencyError("");
    setTaxError("");
  }, [show, mergedDetails, suggestedBaseCost]);

  if (!show) return null;

  const validate = () => {
    let valid = true;
    setCostError("");
    setCurrencyError("");
    setTaxError("");

    if (!getEffectiveBaseCost(cost, mergedDetails)) {
      setCostError("Please enter base cost");
      valid = false;
    }
    if (!getEffectiveCurrency(currency, mergedDetails)) {
      setCurrencyError("Please select currency");
      valid = false;
    }
    if (!getEffectiveTax(tax, mergedDetails)) {
      setTaxError("Please select tax type");
      valid = false;
    }
    if (
      (getEffectiveTax(tax, mergedDetails) === "inclusive" ||
        getEffectiveTax(tax, mergedDetails) === "exclusive") &&
      getEffectiveCurrency(currency, mergedDetails) &&
      !breakdown?.gst_percentage &&
      !gstPercentage
    ) {
      setTaxError("No GST setting found for selected currency.");
      valid = false;
    }

    return valid;
  };

  const handleSave = async () => {
    if (!validate()) return;

    const typeCode = type === "mig" ? 1 : 2;
    const { cost: _cost, ...costPayload } = buildApplicationCostPayload(breakdown);
    const payload = {
      currency: getEffectiveCurrency(currency, mergedDetails),
      gst_status: getEffectiveTax(tax, mergedDetails),
      ...costPayload,
    };

    setSaving(true);
    try {
      const response = await apiUrl.post(
        `/updateApplicationDetail/${appId}/${typeCode}`,
        payload,
      );

      if (response?.data?.status) {
        Swal.fire(
          "Saved",
          response?.data?.message ||
            "Application cost and GST updated successfully.",
          "success",
        );
        onSaved?.();
        onClose();
      } else {
        Swal.fire(
          "Error",
          response?.data?.message || "Failed to update application cost.",
          "error",
        );
      }
    } catch (error) {
      console.error("Failed to save application cost detail", error);
      Swal.fire(
        "Error",
        error?.response?.data?.message ||
          "Failed to update application cost. Ensure updateApplicationDetail API is available.",
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div
        className="modal-backdrop fade show"
        style={{
          display: "block",
          backgroundColor: "rgba(0,0,0,0.5)",
          zIndex: 1060,
        }}
        onClick={onClose}
      />
      <div
        className="modal fade show d-block"
        tabIndex={-1}
        role="dialog"
        style={{ zIndex: 1070 }}
      >
        <div
          className="modal-dialog modal-dialog-centered modal-lg"
          style={{ maxWidth: 820 }}
        >
          <div className="modal-content border-0 rounded-4 shadow">
            <div
              className="modal-header"
              style={{
                background: "#f7f8fa",
                borderBottom: "1px solid #eef2f6",
              }}
            >
              <div>
                <h5 className="modal-title mb-0" style={{ fontWeight: 700 }}>
                  Configure GST
                </h5>
                <div className="text-muted" style={{ fontSize: 12 }}>
                  GST % comes from GST Settings for the selected currency. Choose
                  Inclusive or Exclusive — credits, payments, and totals update
                  after save.
                </div>
              </div>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
                disabled={saving}
              />
            </div>
            <div className="modal-body" style={{ background: "#fafbfc" }}>
              <div className="row g-2 align-items-start">
                <ApplicationCostSection
                  cost={cost}
                  setCost={setCost}
                  currency={currency}
                  setCurrency={setCurrency}
                  tax={tax}
                  setTax={setTax}
                  details={mergedDetails}
                  costError={costError}
                  currencyError={currencyError}
                  taxError={taxError}
                  breakdown={breakdown}
                  effectiveCurrency={effectiveCurrency}
                  gstSetting={gstSetting}
                  gstPercentage={gstPercentage}
                  applyCurrencySelection={applyCurrencySelection}
                  baseInputClassName="form-control acc-form col-lg-5 ml-1"
                  currencySelectClassName="browser-default custom-select p-1 ml-1 col-lg-3 acc-label2"
                  taxSelectClassName="form-select acc-form"
                />
              </div>
            </div>
            <div
              className="modal-footer"
              style={{
                background: "#f7f8fa",
                borderTop: "1px solid #eef2f6",
              }}
            >
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={onClose}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? "Saving..." : "Save GST"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default ApplicationGstSetupModal;
